import type { ParticipantPropertiesPayload } from '@zoom/videosdk';
import { ConnectionState } from '@zoom/videosdk';

import { RemoteParticipantEventHandler } from '../../../core/room/participant/types';
import {
  type PeerShareStateChangePayload,
  type VideoStateChangePayload,
  checkCommandChannelState,
  checkScreenShareSubscribed,
  checkScreenShareUnsubscribed,
  checkUserAudioEnabled,
  checkUserAudioSubscribed,
  checkUserAudioUnsubscribed,
  checkUserVideoEnabled,
  checkVideoSubscribed,
  checkVideoUnsubscribed,
} from './predicates';

/**
 * Maps Zoom SDK’s broad event model to the app’s finer-grained media events.
 *
 * Zoom emits coarse events (e.g., "user-updated") that can represent many state
 * changes—audio/video on/off, mute/unmute, etc.—while our app expects specific
 * events such as "mediaSubscribed" or "mediaEnabled".
 *
 * Structure (3 levels):
 * 1. App event name ("mediaSubscribed", "mediaEnabled", etc.)
 * 2. Media kind ("audio", "video", "data")
 * 3. Zoom event mappings — each with:
 *   - zoomEvent: Zoom SDK event to listen for
 *   - predicate: Filter determining if the Zoom event should invoke the app event
 *
 * Most mappings are 1:1. The exception is video, where "mediaSubscribed"
 * listens to both "peer-video-state-change" and "active-share-change"
 * to support shared logic in useRemoteVideo for camera and screen video.
 */
export const EVENT_CONFIG_MAP = {
  mediaSubscribed: {
    audio: [
      {
        zoomEvent: 'user-updated',
        predicate: (args: ParticipantPropertiesPayload[], participantUserId: number) =>
          checkUserAudioSubscribed(args, participantUserId),
      },
    ],
    video: [
      {
        zoomEvent: 'peer-video-state-change',
        predicate: (args: VideoStateChangePayload, participantUserId: number) =>
          checkVideoSubscribed(args, participantUserId),
      },
      {
        zoomEvent: 'peer-share-state-change',
        predicate: (args: PeerShareStateChangePayload, participantUserId: number) =>
          checkScreenShareSubscribed(args, participantUserId),
      },
    ],
    data: [
      {
        zoomEvent: 'command-channel-status',
        predicate: (connectionState: ConnectionState) =>
          checkCommandChannelState(connectionState, ConnectionState.Connected),
      },
    ],
  },
  mediaUnsubscribed: {
    audio: [
      {
        zoomEvent: 'user-updated',
        predicate: (args: ParticipantPropertiesPayload[], participantUserId: number) =>
          checkUserAudioUnsubscribed(args, participantUserId),
      },
    ],
    video: [
      {
        zoomEvent: 'peer-video-state-change',
        predicate: (args: VideoStateChangePayload, participantUserId: number) =>
          checkVideoUnsubscribed(args, participantUserId),
      },
      {
        zoomEvent: 'peer-share-state-change',
        predicate: (args: PeerShareStateChangePayload, participantUserId: number) =>
          checkScreenShareUnsubscribed(args, participantUserId),
      },
    ],
    data: [
      {
        zoomEvent: 'command-channel-status',
        predicate: (connectionState: ConnectionState) =>
          checkCommandChannelState(connectionState, ConnectionState.Closed),
      },
    ],
  },
  mediaEnabled: {
    audio: [
      {
        zoomEvent: 'user-updated',
        predicate: (args: ParticipantPropertiesPayload[], participantUserId: number) =>
          checkUserAudioEnabled(args, participantUserId),
      },
    ],
    video: [
      {
        zoomEvent: 'user-updated',
        predicate: (args: ParticipantPropertiesPayload[], participantUserId: number) =>
          checkUserVideoEnabled(args, participantUserId),
      },
    ],
    data: [
      {
        zoomEvent: 'command-channel-status',
        predicate: (connectionState: ConnectionState) =>
          checkCommandChannelState(connectionState, ConnectionState.Connected),
      },
    ],
  },
} as const;

// Extract supported events from the eventConfigMap keys, constrained to RemoteParticipantEvent
export type SupportedRemoteParticipantEvent = keyof typeof EVENT_CONFIG_MAP;
export type SupportedRemoteParticipantEventHandler = RemoteParticipantEventHandler<SupportedRemoteParticipantEvent>;
